import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CContainer, 
  CRow, 
  CCol, 
  CCard, 
  CCardHeader, 
  CCardBody, 
  CButton, 
  CSpinner,
  CBadge
} from '@coreui/react';
import { Lock, Unlock } from 'lucide-react';
import api from '../../api/axiosInstance';

const YearWiseQuestionBank = () => {
  const { section, title } = useParams();
  const navigate = useNavigate();

  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maxYear, setMaxYear] = useState(null);

  // User Auth & Subscription Details
  const authToken = localStorage.getItem('authToken');
  const userTier = localStorage.getItem('subscriptionTier') || 'Alpha';
  const isSubActive = localStorage.getItem('isSubscriptionActive') === 'true';

  // Tier Hierarchy (Beeta, Gamma, Delta, Sigma members can access)
  const allowedTiers = ['Beeta', 'Gamma', 'Delta', 'Sigma'];
  const hasBeetaAccess = isSubActive && allowedTiers.includes(userTier);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 9;

  const defaultThumbnail = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='350' height='200' viewBox='0 0 350 200'%3E%3Crect width='100%25' height='100%25' fill='%23f1f5f9'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='16px' fill='%2394a3b8'%3EThumbnail Image%3C/text%3E%3C/svg%3E";

  const cleanTitle = title ? title.replace(/-/g, ' ') : '';

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchYearWisePapers();
  }, [section, title, currentPage]);

  const fetchYearWisePapers = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await api.get(
        `/api/content/GetYearWisePapers?section=${section}&category=${encodeURIComponent(cleanTitle)}&page=${currentPage}&pageSize=${itemsPerPage}`,
        { skipAuthCheck: true }
      );

      if (res.data) {
        const fetchedPapers = res.data.papers || res.data.data || [];
        setPapers(fetchedPapers);

        // Sabse Latest Year Pata lagayein
        if (fetchedPapers.length > 0) {
          const years = fetchedPapers.map(p => parseInt(p.year || 0)).filter(y => !isNaN(y));
          if (years.length > 0) {
            setMaxYear(Math.max(...years));
          }
        }

        const total = res.data.totalCount || res.data.total || 0;
        setTotalPages(Math.ceil(total / itemsPerPage) || 1);
      }
    } catch (err) {
      console.error("Error fetching yearwise papers:", err);
      setError("Failed to load year-wise question papers.");
    } finally {
      setLoading(false);
    }
  };

  const handleGetPaper = (paper) => {
    const paperYear = parseInt(paper.year || 0);
    const isLatest = paperYear === maxYear;

    // 1. LATEST YEAR ACCESS LOGIC (Sirf Login Required)
    if (isLatest) {
      if (!authToken) {
        navigate('/login', { state: { from: `/${section}/${title}/year-wise/${paper.year || paper.id}` } });
        return;
      }
      // Logged in user can view latest year
      navigate(`/${section}/${title}/year-wise/${paper.year || paper.id}`);
      return;
    }

    // 2. OLD YEARS ACCESS LOGIC (Beeta ya Higher Tier Required)
    if (!authToken) {
       navigate('/login', { state: { from: `/${section}/${title}/year-wise/${paper.year || paper.id}` } });
      return;
    }

    if (!hasBeetaAccess) {
      navigate('/profile'); // User profile redirect to upgrade plan
      return;
    }

    // Access Granted
    navigate(`/${section}/${title}/year-wise/${paper.year || paper.id}`);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5 py-5">
        <CSpinner color="primary" size="lg" />
        <p className="text-muted mt-2">Loading Previous Year Questions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <CContainer className="text-center mt-5 py-4">
        <h5 className="text-danger mb-3">{error}</h5>
        <CButton color="primary" size="sm" onClick={() => navigate(-1)}>Go Back</CButton>
      </CContainer>
    );
  }

  return (
    <>
      {/* 🌟 FULL-WIDTH EDGE-TO-EDGE BLUE BANNER */}
      <div 
        className="w-100 text-white text-center py-5 shadow-sm mb-4" 
        style={{ 
          backgroundColor: 'rgb(0, 33, 71)',
          marginLeft: 'calc(-50vw + 50%)',
          marginRight: 'calc(-50vw + 50%)',
          width: '100vw',
          position: 'relative',
          left: '50%',
          right: '50%',
          transform: 'translateX(-50%)'
        }}
      >
        <div className="container py-2 px-3">
          <h1 className="fw-bold mb-2   display-6" style={{color:'yellow'}}>
            {cleanTitle.toUpperCase()} Previous Year Questions
          </h1>
          <p className="lead m-0 opacity-90 text-light fs-6">
            {section.toUpperCase()} • Year-wise Question Papers & Solutions
          </p>
        </div>
      </div>

      {/* 🚀 MAIN CONTENT GRID */}
      <CContainer className="pb-5">
        {papers.length > 0 ? (
          <CRow className="g-4">
            {papers.map((paper) => {
              const paperYear = parseInt(paper.year || 0);
              const isLatest = paperYear === maxYear;
              // Lock Status check logic
              const isLocked = isLatest ? !authToken : !hasBeetaAccess;

              return (
                <CCol lg={4} md={6} sm={12} key={paper.id || paper.year}>
                  <CCard className="h-100 border-2 border-dark shadow-sm rounded-3 overflow-hidden custom-card-hover">
                    
                    {/* Header: Title with Year */}
                    <CCardHeader className="bg-white border-bottom border-2 border-dark text-center py-2">
                      <h5 className="fw-bold m-0 text-dark">
                        {paper.title || `${cleanTitle.toUpperCase()} ${paper.year}`}
                      </h5>
                    </CCardHeader>

                    {/* Body: Thumbnail Image */}
                    <CCardBody className="p-0 bg-light d-flex align-items-center justify-content-center" style={{ height: '180px' }}>
                      <img 
                        src={paper.thumbnail || defaultThumbnail} 
                        alt={paper.title || 'Paper Thumbnail'} 
                        className="w-100 h-100 object-fit-cover"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = defaultThumbnail;
                        }}
                      />
                    </CCardBody>

                    {/* Footer: Action Button with Requirement Text */}
                    <div className="p-0">
                      <CButton 
                        color={isLocked ? "secondary" : "danger"}
                        className="w-100 fw-bold text-white rounded-0 py-2 d-flex align-items-center justify-content-center gap-2 border-top border-2 border-dark"
                        style={{ backgroundColor: isLocked ? '#6c757d' : '#c1121f' }}
                        onClick={() => handleGetPaper(paper)}
                      >
                        <span>
                          {isLatest 
                            ? (isLocked ? "Get Paper (Login Required)" : "Get Paper") 
                            : (isLocked ? "Unlock Paper (Beeta Required)" : "Get Paper")
                          }
                        </span>
                        {isLocked ? <Lock size={16} /> : <Unlock size={16} />}
                      </CButton>
                    </div>

                  </CCard>
                </CCol>
              );
            })}
          </CRow>
        ) : (
          <div className="alert alert-warning text-center">No question papers available for this category.</div>
        )}

        {/* BOTTOM PAGINATION BAR */}
        {totalPages > 0 && (
          <div className="mt-5 p-3 bg-white border border-2 border-dark rounded-3 d-flex align-items-center justify-content-center gap-3 flex-wrap">
            <span className="fw-bold text-dark">Page No:</span>
            
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`btn btn-sm fw-bold border ${currentPage === page ? 'btn-dark text-white' : 'btn-light text-dark'}`}
                  style={{ minWidth: '35px' }}
                >
                  {page}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Card Hover CSS */}
        <style>{`
          .custom-card-hover {
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }
          .custom-card-hover:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 16px rgba(0,0,0,0.2) !important;
          }
        `}</style>
      </CContainer>
    </>
  );
};

export default YearWiseQuestionBank;